import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button} from 'react-bootstrap';

import TrayPreviewItem from './TrayPreviewItem';

import FastModal from '../../components/FastModal';

import withIntl from '../../lib/withIntl';

import './TrayCalibrationInfoPanel.scss';
import {TrayType} from '../../constants/dataEnum';

function Illustration({rollPitchYaw, thresholdRPY}) {
  return (
    <div className="illustration">
      <div className="illustration__image-container">
        <img
          className="illustration__image"
          alt="./svg/tray-calibration/pitch-roll-yaw-illustration.svg"
          src="./svg/tray-calibration/pitch-roll-yaw-illustration.svg"
          width="400"
        />
      </div>
      <div className="illustration__subtitle">
        <span>
          Pitch: &nbsp;
          <strong className={`${rollPitchYaw?.y > thresholdRPY?.y ? 'red' : ''}`}>
            {rollPitchYaw ? (
              <span>
                {rollPitchYaw.y}
                &#176;
              </span>
            ) : (
              '-'
            )}
          </strong>
        </span>
        <span>
          Roll: &nbsp;
          <strong className={`${rollPitchYaw?.x > thresholdRPY?.x ? 'red' : ''}`}>
            {rollPitchYaw ? (
              <span>
                {rollPitchYaw.x}
                &#176;
              </span>
            ) : (
              '-'
            )}
          </strong>
        </span>
        <span>
          Yaw: &nbsp;
          <strong className={`${rollPitchYaw?.z > thresholdRPY?.z ? 'red' : ''}`}>
            {rollPitchYaw ? (
              <span>
                {rollPitchYaw.z}
                &#176;
              </span>
            ) : (
              '-'
            )}
          </strong>
        </span>
      </div>
    </div>
  );
}

Illustration.propTypes = {
  rollPitchYaw: PropTypes.object.isRequired,
  thresholdRPY: PropTypes.object.isRequired,
};

function TrayCalibrationInfoPanel({trayCalibrationState, selectedDeckInsert, isCalibrated, intl}) {
  const [showIllustration, setShowIllustration] = useState(false);

  const {deckInserts, id, lastCalibrated, rollPitchYaw, thresholdRPY, trayType} = trayCalibrationState;

  const getTrayType = (type) => {
    switch (type) {
      case TrayType.NORMAL_CADDY:
        return intl.formatMessage({id: 'trayCalibrationContainer.trayType.normalCaddy'});
      case TrayType.DOUBLE_CADDY:
        return intl.formatMessage({id: 'trayCalibrationContainer.trayType.doubleCaddy'});
      case TrayType.HIGH_VOLUME_CADDY:
        return intl.formatMessage({id: 'trayCalibrationContainer.trayType.highVolumeCaddy'});
      default:
        return '';
    }
  };

  return (
    <div className="tray-calibration-info-panel">
      <div className="tray-calibration-info-panel__header">
        <div className="general-information">
          <div className="general-information__header">
            <span>{intl.formatMessage({id: 'trayCalibrationContainer.generalInformation.label'})}</span>
          </div>
          <div className="general-information__body">
            <div className="general-information__item">
              <div className="general-information__item__label">
                <span>{intl.formatMessage({id: 'trayCalibrationContainer.trayStatus.label'})}</span>
              </div>
              <div
                className={`tray-rotation__item__value calibration-status ${isCalibrated ? 'calibrated' : 'uncalibrated'}`}
              >
                <span>
                  {isCalibrated
                    ? intl.formatMessage({id: 'trayCalibrationContainer.trayStatus.calibrated'})
                    : intl.formatMessage({id: 'trayCalibrationContainer.trayStatus.uncalibrated'})}
                </span>
              </div>
            </div>
            <div className="general-information__item">
              <div className="general-information__item__label">
                <span>{intl.formatMessage({id: 'trayCalibrationContainer.trayId.label'})}</span>
              </div>
              <strong className="tray-rotation__item__value">{`#${id}`}</strong>
            </div>
            <div className="general-information__item">
              <div className="general-information__item__label">
                <span>{intl.formatMessage({id: 'trayCalibrationContainer.trayType.label'})}</span>
              </div>
              <span className={`tray-rotation__item__value tray-type ${trayType}`}>{getTrayType(trayType)}</span>
            </div>
          </div>
        </div>
        <div className="tray-rotation">
          <div className="tray-rotation__header">
            <span>{intl.formatMessage({id: 'trayCalibrationContainer.trayRotation.label'})}</span>
            <Button className="tray-rotation__button" onClick={() => setShowIllustration(true)}>
              {intl.formatMessage({id: 'trayCalibrationContainer.trayRotation.button'})}
            </Button>
          </div>
          <FastModal
            className="tray-rotation__modal"
            isValid
            show={showIllustration}
            header={intl.formatMessage({id: 'trayCalibrationContainer.modal.trayRotation.title'})}
            content={<Illustration rollPitchYaw={rollPitchYaw} thresholdRPY={thresholdRPY} />}
            onClose={() => setShowIllustration(false)}
            closeDisabled={false}
          />
          <div className="general-information__body">
            <div className="tray-rotation__item">
              <div className="tray-rotation__item__label">
                {intl.formatMessage({id: 'trayCalibrationContainer.pitch.label'})}
              </div>
              <strong className={`tray-rotation__item__value ${rollPitchYaw?.y > thresholdRPY?.y ? 'red' : ''}`}>
                {rollPitchYaw ? (
                  <span>
                    {rollPitchYaw.y}
                    &#176;
                  </span>
                ) : (
                  '-'
                )}
              </strong>
            </div>
            <div className="tray-rotation__item">
              <div className="tray-rotation__item__label">
                {intl.formatMessage({id: 'trayCalibrationContainer.roll.label'})}
              </div>
              <strong className={`tray-rotation__item__value ${rollPitchYaw?.x > thresholdRPY?.x ? 'red' : ''}`}>
                {rollPitchYaw ? (
                  <span>
                    {rollPitchYaw.x}
                    &#176;
                  </span>
                ) : (
                  '-'
                )}
              </strong>
            </div>
            <div className="tray-rotation__item">
              <div className="tray-rotation__item__label">
                {intl.formatMessage({id: 'trayCalibrationContainer.yaw.label'})}
              </div>
              <strong className={`tray-rotation__item__value ${rollPitchYaw?.z > thresholdRPY?.z ? 'red' : ''}`}>
                {rollPitchYaw ? (
                  <span>
                    {rollPitchYaw.z}
                    &#176;
                  </span>
                ) : (
                  '-'
                )}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <hr />

      <div className="tray-calibration-info-panel__body">
        <div className="tray-preview">
          <div className="tray-preview__header">
            <span className="tray-preview__header__title">Tray Preview</span>
            <div className="tray-preview__header__info">
              <span className="tray-preview__header__info--label">
                {intl.formatMessage({id: 'trayCalibrationContainer.lastCalibration.label'})}
              </span>
              <span className="tray-preview__header__info--value">{new Date(lastCalibrated).toLocaleString()}</span>
            </div>
          </div>
          <div className="tray-preview__body">
            {deckInserts.map((deckValue) => (
              <TrayPreviewItem
                key={deckValue.id}
                deckValue={deckValue}
                selectedDeckInsert={selectedDeckInsert === deckValue.id}
                type={trayType}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

TrayCalibrationInfoPanel.propTypes = {
  trayCalibrationState: PropTypes.object.isRequired,
  selectedDeckInsert: PropTypes.bool.isRequired,
  isCalibrated: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
};

export default withIntl(TrayCalibrationInfoPanel);
